/**
 * Web chat tool-card for the connector WRITE tools (M3.2 / M3.3): all of which
 * are approval-gated:
 *
 * - sendEmail              → email summary (To / Subject / Message).
 * - postSlackMessage       → Slack message summary (Channel / Message).
 * - triggerZapierWorkflow  → Zapier workflow summary.
 * - triggerMakeScenario    → Make scenario summary.
 *
 * Pending → renders {@link ApprovalBanner} (localized confirm copy). Completed →
 * a "sent / triggered" confirmation with the same field summary. Web-only; a
 * `.native.tsx` sibling renders iOS/Android. Copy from `connectors.card.*`.
 */
import React, { type FC } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import { safeGet } from './types';
import type { ToolCardProps } from './types';

const TITLE_KEY: Record<string, string> = {
  sendEmail: 'connectors.card.sendEmailTitle',
  postSlackMessage: 'connectors.card.postSlackTitle',
  triggerZapierWorkflow: 'connectors.card.zapierTitle',
  triggerMakeScenario: 'connectors.card.makeTitle',
};

const DONE_KEY: Record<string, string> = {
  sendEmail: 'connectors.card.sentEmail',
  postSlackMessage: 'connectors.card.postedSlack',
  triggerZapierWorkflow: 'connectors.card.triggered',
  triggerMakeScenario: 'connectors.card.triggered',
};

const ICON_FOR_TOOL: Record<string, string> = {
  sendEmail: 'mail',
  postSlackMessage: 'chat',
  triggerZapierWorkflow: 'bolt',
  triggerMakeScenario: 'account_tree',
};

interface Field {
  label: string;
  value: string;
}

function buildFields(
  t: TFunction,
  toolName: string,
  args: unknown,
  result: unknown,
): Field[] {
  const get = (...keys: string[]) =>
    safeGet<string>(args, ...keys) ?? safeGet<string>(result, ...keys);
  const fields: Field[] = [];

  if (toolName === 'sendEmail') {
    const to = get('to') || get('recipient') || get('email');
    const subject = get('subject');
    const body = get('body') || get('text') || get('message');
    if (to) fields.push({ label: t('connectors.card.fieldTo'), value: to });
    if (subject) fields.push({ label: t('connectors.card.fieldSubject'), value: subject });
    if (body) fields.push({ label: t('connectors.card.fieldMessage'), value: body });
  } else if (toolName === 'postSlackMessage') {
    const channel = get('channel') || get('channelName');
    const text = get('text') || get('message') || get('body');
    if (channel) fields.push({ label: t('connectors.card.fieldChannel'), value: channel });
    if (text) fields.push({ label: t('connectors.card.fieldMessage'), value: text });
  } else if (toolName === 'triggerZapierWorkflow') {
    const wf = get('workflow') || get('workflowName') || get('name') || get('zapId') || get('connector');
    if (wf) fields.push({ label: t('connectors.card.fieldWorkflow'), value: wf });
  } else if (toolName === 'triggerMakeScenario') {
    const sc = get('scenario') || get('scenarioName') || get('name') || get('scenarioId') || get('connector');
    if (sc) fields.push({ label: t('connectors.card.fieldScenario'), value: sc });
  }

  return fields;
}

export const ConnectorActionCard: FC<ToolCardProps> = ({
  toolName,
  args,
  result,
  stage,
  approvalId,
}) => {
  const { t } = useTranslation('common');
  const title = t(TITLE_KEY[toolName] ?? 'connectors.card.actionLabel');
  const fields = buildFields(t, toolName, args, result);

  const fieldList = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {fields.map((field, index) => (
        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{field.label}</span>
          <span
            style={{
              fontSize: 14,
              lineHeight: '20px',
              color: 'var(--text-primary)',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            dir="auto"
          >
            {field.value}
          </span>
        </div>
      ))}
    </div>
  );

  // Pending approval → header + summary + approval controls.
  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: '16px 18px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MaterialSymbol
              name={ICON_FOR_TOOL[toolName] ?? 'extension'}
              size={15}
              style={{ color: 'var(--text-tertiary)' }}
            />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
              {t('connectors.card.actionLabel')}
            </span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
            {title}
          </div>
          {fields.length > 0 ? fieldList : null}
        </div>
        <ApprovalBanner
          approvalId={approvalId}
          toolName={toolName}
          description={t('connectors.card.confirm')}
        />
      </CardShell>
    );
  }

  // Completed → confirmation + summary.
  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="check_circle" size={16} style={{ color: 'var(--success, #1a7f37)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t(DONE_KEY[toolName] ?? 'connectors.card.triggered')}
          </span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
          {title}
        </div>
        {fields.length > 0 ? fieldList : null}
      </div>
    </CardShell>
  );
};
